/**
 * Mental Map room — the two modes of the activity.
 *
 *   Draw    — each person works in their own area, from the legend. Nobody
 *             else sees it until they show it, or the host reveals everyone.
 *   Present — up to four maps on screen at once; the rest wait in a tray and
 *             are dragged onto the stage. "Whole room" shows every visible map
 *             on one canvas, each in its own area.
 *
 * The room is given its state and actions (`MapRoom`), so the same screen runs
 * on the lab preview today and on a live session's stored responses next.
 */

import { useMemo, useState, type DragEvent } from 'react';
import { Check, Download, Eye, EyeOff, Link2, Maximize2, Minimize2, Star, X } from 'lucide-react';
import { RoomCountdown } from '@/components/prioritization/session/RoomCountdown';
import { ROOM_TIMER_CHOICES } from '@/lib/sessions/roomRules';

import { Modal } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { downloadMap, downloadSession } from './download';
import { MAP_EXAMPLES } from './examples';
import { Legend } from './Legend';
import { MapEditor, MapView, RoomView } from './MapCanvas';
import { SaveToSpace } from './SaveToSpace';
import { SharedModels, type SharedRoom } from './SharedModels';
import {
  EMPTY_MAP,
  STAGE_MAX,
  addToStage,
  promoteOnStage,
  removeFromStage,
  stageLayout,
  type MapDoc,
} from './model';

export interface MapRoomPerson {
  id: string;
  name: string;
  /** Null while this person's map is still private to them. */
  doc: MapDoc | null;
  /** They have said they are finished. Known even while their map is private. */
  done?: boolean;
  /** They have drawn at least one shape. Known even while their map is private. */
  started?: boolean;
}

export interface MapRoom {
  /** What everyone was asked to map. */
  prompt: string;
  meId: string;
  isHost: boolean;
  myDoc: MapDoc;
  /** True once I have chosen to show my map before the reveal. */
  myMapShown: boolean;
  /** True once the host has revealed every map. */
  revealed: boolean;
  /** Everyone in the room, me included. */
  people: MapRoomPerson[];
  /** The link others open to join. Absent in the preview, which has no link. */
  joinUrl?: string;
  /** The room's shared models. Absent where the room cannot hold any. */
  shared?: SharedRoom;
  /** Where the room's changes stand. Absent where nothing is stored (the preview). */
  saveState?: 'saved' | 'saving' | 'failed';
  /** True when the person is signed in and can keep a model in one of their Spaces. */
  canSaveToSpace?: boolean;
  /** Running the exercise. Absent where the room has no such controls. */
  myDone?: boolean;
  setMyDone?: (done: boolean) => void;
  /** When the countdown runs out, as a time on this device's clock; null when none is running. */
  timerEndsAt?: number | null;
  /** Host only: start a countdown of this many seconds, or stop it with null. */
  setTimer?: (seconds: number | null) => void;
  saveMyDoc: (doc: MapDoc) => void;
  setMyMapShown: (shown: boolean) => void;
  revealAll: () => void;
}

type Mode = 'draw' | 'present' | 'shared';

const PERSON_DRAG_TYPE = 'application/x-mental-map-person';

const EYEBROW = 'font-mono text-xs uppercase tracking-wider text-[var(--text-dim)]';

// ── Running the exercise ─────────────────────────────────────────────────────

/** "4:32 left", counting down every second; "Time is up" once it passes. */
/** What one person is up to, in words that give away nothing about their map. */
function personStatus(person: MapRoomPerson): string {
  if (person.done) return 'Done';
  if (person.started) return 'Drawing';
  return 'Not started';
}

// ── Examples ─────────────────────────────────────────────────────────────────

function ExamplesModal({
  open,
  onClose,
  onUse,
  hasMap,
  onBlank,
}: {
  open: boolean;
  onClose: () => void;
  onUse: (doc: MapDoc) => void;
  /** True once the person has drawn something. */
  hasMap: boolean;
  onBlank: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      label="Examples"
      title="What a mental map looks like"
      description="A few shapes that each mean something, joined by lines that each mean something. There is no right answer: it is how you see it."
      // The way in that needs no example stays in view however far the examples scroll.
      footer={
        hasMap ? (
          <>
            <Button variant="secondary" onClick={onBlank}>
              Start over with a blank map
            </Button>
            <Button variant="cta" onClick={onClose}>
              Back to my map
            </Button>
          </>
        ) : (
          <>
            <span className="mr-auto font-body text-sm text-[var(--text-sec)]">The examples are only ideas. You can start from nothing.</span>
            <Button variant="cta" onClick={onBlank}>
              Start with a blank map
            </Button>
          </>
        )
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {MAP_EXAMPLES.map((ex) => (
          <article key={ex.id} className="overflow-hidden rounded-xl border border-[var(--rule)]">
            <div className="h-48">
              <MapView doc={ex.doc} />
            </div>
            <div className="border-t border-[var(--rule)] p-3">
              <p className={EYEBROW}>{ex.prompt}</p>
              <h3 className="mt-1 font-heading text-base font-semibold text-[var(--text-pri)]">{ex.title}</h3>
              <p className="mt-1 font-body text-sm text-[var(--text-sec)]">{ex.reading}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => onUse(ex.doc)}>
                Start from this one
              </Button>
            </div>
          </article>
        ))}
      </div>
    </Modal>
  );
}

// ── Presentation ─────────────────────────────────────────────────────────────

function Stage({ room }: { room: MapRoom }) {
  const visible = useMemo(() => room.people.filter((p) => p.doc !== null), [room.people]);
  // One stable list for the whole-room canvas: a new array on every render
  // would redraw it (and lose the viewer's pan and zoom) each time the room polls.
  const roomMaps = useMemo(() => visible.map((p) => ({ id: p.id, name: p.name, doc: p.doc! })), [visible]);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [wholeRoom, setWholeRoom] = useState(false);
  const [dropSlot, setDropSlot] = useState<number | 'stage' | null>(null);

  // Until someone arranges the stage, show the first maps that are visible.
  const stage = (picked ?? visible.slice(0, STAGE_MAX).map((p) => p.id)).filter((id) => visible.some((p) => p.id === id));
  const onStage = stage.map((id) => visible.find((p) => p.id === id)!);
  const focusedPerson = focused ? onStage.find((p) => p.id === focused) : undefined;
  const tiles = focusedPerson ? [focusedPerson] : onStage;
  const layout = stageLayout(tiles.length);

  /** Show one person's map on its own, from anywhere. */
  const showOnly = (id: string) => {
    setWholeRoom(false);
    setPicked(addToStage(stage, id));
    setFocused(id);
  };

  const drop = (event: DragEvent, slot?: number) => {
    const id = event.dataTransfer.getData(PERSON_DRAG_TYPE);
    setDropSlot(null);
    if (!id) return;
    event.preventDefault();
    event.stopPropagation();
    setFocused(null);
    setPicked(addToStage(stage, id, slot));
  };
  const allowDrop = (event: DragEvent, slot: number | 'stage') => {
    if (!event.dataTransfer.types.includes(PERSON_DRAG_TYPE)) return;
    event.preventDefault();
    event.stopPropagation();
    setDropSlot(slot);
  };

  return (
    <div className="flex h-full min-h-0 w-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-2">
          <Button variant={wholeRoom ? 'secondary' : 'cta'} size="sm" onClick={() => setWholeRoom(false)}>
            Stage
          </Button>
          <Button variant={wholeRoom ? 'cta' : 'secondary'} size="sm" onClick={() => setWholeRoom(true)}>
            Whole room
          </Button>
          <span className="ml-2 font-body text-sm text-[var(--text-dim)]">
            {wholeRoom
              ? `${visible.length} of ${room.people.length} maps visible, each in its own area — click a name to see that map on its own`
              : focusedPerson
                ? `Showing only ${focusedPerson.name}`
                : `${onStage.length} on stage — up to ${STAGE_MAX} at once`}
          </span>
          {!wholeRoom && focusedPerson && (
            <Button variant="secondary" size="sm" onClick={() => setFocused(null)}>
              Back to everyone on stage
            </Button>
          )}
        </div>

        {wholeRoom ? (
          <div className="min-h-0 flex-1">
            <RoomView maps={roomMaps} onOpen={showOnly} />
          </div>
        ) : (
          <div
            className={`grid min-h-0 flex-1 gap-2 bg-[var(--surface)] p-2 ${dropSlot === 'stage' ? 'outline outline-1 -outline-offset-4 outline-[var(--text-ter)]' : ''}`}
            style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gridTemplateRows: 'repeat(2, minmax(0, 1fr))' }}
            onDragOver={(e) => allowDrop(e, 'stage')}
            onDragLeave={() => setDropSlot(null)}
            onDrop={(e) => drop(e)}
          >
            {tiles.length === 0 && (
              <div className="col-span-6 row-span-2 flex items-center justify-center px-6 text-center font-body text-[var(--text-dim)]">
                No maps are visible yet. A map appears here when its owner shows it, or when the host reveals all maps.
              </div>
            )}
            {tiles.map((person, i) => (
              <section
                key={person.id}
                className={`flex min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border bg-[var(--bg-raised)] ${
                  dropSlot === i ? 'border-[var(--text-pri)]' : 'border-[var(--rule)]'
                }`}
                style={{ gridColumn: layout[i].column, gridRow: layout[i].row }}
                onDragOver={(e) => allowDrop(e, i)}
                onDrop={(e) => drop(e, focusedPerson ? undefined : i)}
              >
                <header className="flex items-center gap-2 border-b border-[var(--rule)] px-3 py-1.5">
                  <h3 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-[var(--text-pri)]">
                    {person.name}
                    {person.id === room.meId && <span className="ml-1 font-body font-normal text-[var(--text-dim)]"> (you)</span>}
                  </h3>
                  {!focusedPerson && onStage.length > 1 && i > 0 && (
                    <Button title="Make this the large one" aria-label={`Make ${person.name}'s map the large one`} variant="ghost" size="sm" className="!p-1" onClick={() => setPicked(promoteOnStage(stage, person.id))}>
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  {onStage.length > 1 && (
                    <Button
                      title={focusedPerson ? 'Back to all maps on stage' : 'Fill the screen with this map'}
                      aria-label={focusedPerson ? 'Back to all maps on stage' : `Fill the screen with ${person.name}'s map`}
                      variant="ghost" size="sm" className="!p-1"
                      onClick={() => setFocused(focusedPerson ? null : person.id)}
                    >
                      {focusedPerson ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                  )}
                  <Button
                    title="Save this map as a picture"
                    aria-label={`Save ${person.name}'s map as a picture`}
                    variant="ghost" size="sm" className="!p-1"
                    onClick={() => downloadMap(person.doc!, `${person.name} — ${room.prompt}`)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    title="Take off the stage"
                    aria-label={`Take ${person.name}'s map off the stage`}
                    variant="ghost" size="sm" className="!p-1"
                    onClick={() => {
                      setFocused(null);
                      setPicked(removeFromStage(stage, person.id));
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </header>
                <div className="min-h-0 flex-1">
                  <MapView doc={person.doc!} interactive />
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      <aside className="flex w-60 shrink-0 flex-col overflow-y-auto border-l border-[var(--rule)] bg-[var(--bg-raised)]">
        <div className="px-4 pt-4">
          <h2 className="font-heading text-base font-semibold text-[var(--text-pri)]">Everyone</h2>
          <p className="mt-1 font-body text-xs text-[var(--text-dim)]">
            {wholeRoom ? 'Click a map to see it on its own.' : 'Drag a map onto the stage, or click it.'}
          </p>
        </div>
        <ul className="space-y-2 p-3">
          {room.people.map((person) => {
            const isOnStage = stage.includes(person.id);
            const label = person.id === room.meId ? `${person.name} (you)` : person.name;
            if (person.doc === null) {
              return (
                <li key={person.id} className="rounded-lg border border-dashed border-[var(--rule)] px-3 py-2">
                  <p className="font-heading text-sm font-semibold text-[var(--text-dim)]">{label}</p>
                  <p className="flex items-center gap-1 font-body text-xs text-[var(--text-dim)]">
                    <EyeOff className="h-3 w-3" /> Still private
                    {person.done !== undefined && ` · ${personStatus(person)}`}
                  </p>
                </li>
              );
            }
            return (
              <li key={person.id}>
                <Button
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(PERSON_DRAG_TYPE, person.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onClick={() => {
                    if (wholeRoom) {
                      showOnly(person.id);
                      return;
                    }
                    setFocused(null);
                    setPicked(isOnStage ? removeFromStage(stage, person.id) : addToStage(stage, person.id));
                  }}
                  variant="ghost" size="sm" className={`!block w-full cursor-grab overflow-hidden rounded-lg border !p-0 text-left !font-normal ${
                    isOnStage ? 'border-[var(--text-pri)]' : 'border-[var(--rule)] hover:border-[var(--rule-bright)]'
                  }`}
                >
                  <div className="pointer-events-none h-24">
                    <MapView doc={person.doc} />
                  </div>
                  <div className="flex items-center justify-between border-t border-[var(--rule)] px-3 py-1.5">
                    <span className="truncate font-heading text-sm font-semibold text-[var(--text-pri)]">{label}</span>
                    {isOnStage && <span className={EYEBROW}>On stage</span>}
                  </div>
                </Button>
              </li>
            );
          })}
        </ul>
      </aside>
    </div>
  );
}

// ── Room ─────────────────────────────────────────────────────────────────────

export function MentalMapRoom({ room }: { room: MapRoom }) {
  const [mode, setMode] = useState<Mode>('draw');
  const [examplesOpen, setExamplesOpen] = useState(room.myDoc.shapes.length === 0);
  // Loading an example replaces the editor's starting map, so it remounts.
  const [editorKey, setEditorKey] = useState(0);
  const [startDoc, setStartDoc] = useState<MapDoc>(room.myDoc);
  const [pendingExample, setPendingExample] = useState<MapDoc | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [savingToSpace, setSavingToSpace] = useState(false);

  const copyLink = async () => {
    if (!room.joinUrl) return;
    try {
      await navigator.clipboard.writeText(room.joinUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard blocked: the address bar holds the same link.
    }
  };

  const mineVisible = room.revealed || room.myMapShown;
  const visibleCount = room.people.filter((p) => p.doc !== null).length;

  const loadExample = (doc: MapDoc) => {
    setStartDoc(doc);
    setEditorKey((k) => k + 1);
    room.saveMyDoc(doc);
    setPendingExample(null);
    setExamplesOpen(false);
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-[var(--bg-raised)] text-[var(--text-pri)]">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[var(--rule)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className={EYEBROW}>Mental map</p>
          <h1 className="truncate font-heading text-lg font-semibold">{room.prompt}</h1>
        </div>

        <div className="flex items-center gap-1" role="tablist" aria-label="Mode">
          <Button role="tab" aria-selected={mode === 'draw'} variant={mode === 'draw' ? 'cta' : 'secondary'} size="sm" onClick={() => setMode('draw')}>
            Draw
          </Button>
          <Button role="tab" aria-selected={mode === 'present'} variant={mode === 'present' ? 'cta' : 'secondary'} size="sm" onClick={() => setMode('present')}>
            Present ({visibleCount})
          </Button>
          {room.shared && (
            <Button role="tab" aria-selected={mode === 'shared'} variant={mode === 'shared' ? 'cta' : 'secondary'} size="sm" onClick={() => setMode('shared')}>
              Shared model{room.shared.models.length > 0 ? ` (${room.shared.models.length})` : ''}
            </Button>
          )}
        </div>

        {room.timerEndsAt != null && <RoomCountdown endsAt={room.timerEndsAt} />}

        {room.people.some((p) => p.done !== undefined) && (
          <span
            className="font-body text-sm text-[var(--text-sec)]"
            title={room.people.map((p) => `${p.name}: ${personStatus(p)}`).join('\n')}
          >
            {room.people.filter((p) => p.done).length} of {room.people.length} done
          </span>
        )}

        {mode === 'draw' && room.setMyDone && (
          <Button
            variant={room.myDone ? 'cta' : 'secondary'}
            size="sm"
            aria-pressed={room.myDone}
            onClick={() => room.setMyDone?.(!room.myDone)}
            title={room.myDone ? 'You have said you are done. Click if you want more time.' : 'Tell the room you have finished drawing. You can still change your map.'}
          >
            <Check className="h-4 w-4" /> {room.myDone ? 'Done' : "I'm done"}
          </Button>
        )}

        {room.isHost && room.setTimer && (
          <span className="inline-flex items-center gap-1" role="group" aria-label="Timer">
            {room.timerEndsAt != null ? (
              <Button variant="secondary" size="sm" onClick={() => room.setTimer?.(null)}>
                Stop timer
              </Button>
            ) : (
              <>
                <span className="font-body text-xs text-[var(--text-dim)]">Timer</span>
                {ROOM_TIMER_CHOICES.map((choice) => (
                  <Button key={choice.seconds} variant="secondary" size="sm" className="!px-2" onClick={() => room.setTimer?.(choice.seconds)}>
                    {choice.label}
                  </Button>
                ))}
              </>
            )}
          </span>
        )}

        <Button variant="secondary" size="sm" onClick={() => setExamplesOpen(true)}>
          Examples
        </Button>

        {mode === 'draw' && (
          <Button
            variant="secondary"
            size="sm"
            disabled={room.myDoc.shapes.length === 0}
            onClick={() => downloadMap(room.myDoc, room.prompt)}
            title="Save your map as a picture you can keep or drop into slides"
          >
            <Download className="h-4 w-4" /> Save as picture
          </Button>
        )}

        {mode === 'draw' && room.canSaveToSpace && (
          <Button
            variant="secondary"
            size="sm"
            disabled={room.myDoc.shapes.length === 0}
            onClick={() => setSavingToSpace(true)}
            title="Keep this map, under a name, in your own space or a team space"
          >
            Save to a space
          </Button>
        )}

        {mode !== 'draw' && (
          <Button
            variant="secondary"
            size="sm"
            title="One file with every shared model and every map that has been shown — to send round afterwards, or print as a PDF"
            onClick={() =>
              downloadSession({
                prompt: room.prompt,
                date: new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
                people: room.people.map((p) => ({ name: p.name, doc: p.doc })),
                models: room.shared?.models ?? [],
              })
            }
          >
            <Download className="h-4 w-4" /> Save the session
          </Button>
        )}

        {room.saveState && (
          <span
            role="status"
            className={`font-body text-xs ${room.saveState === 'failed' ? 'text-[var(--coral)]' : 'text-[var(--text-dim)]'}`}
            title="Everything you draw here is kept in this room. Come back to the same link to find it."
          >
            {room.saveState === 'saved' ? 'Saved' : room.saveState === 'saving' ? 'Saving…' : 'Not saved'}
          </span>
        )}

        {room.joinUrl && (
          <Button variant="secondary" size="sm" onClick={() => void copyLink()} title="Anyone with this link can join with just a name">
            {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {linkCopied ? 'Link copied' : 'Copy join link'}
          </Button>
        )}

        {room.revealed ? (
          <span className="inline-flex items-center gap-1.5 font-body text-sm text-[var(--text-sec)]">
            <Eye className="h-4 w-4" /> All maps are revealed
          </span>
        ) : (
          <>
            <Button
              variant={mineVisible ? 'cta' : 'secondary'} size="sm"
              aria-pressed={mineVisible}
              onClick={() => room.setMyMapShown(!room.myMapShown)}
              title={mineVisible ? 'Everyone can see your map. Click to make it private again.' : 'Only you can see your map.'}
            >
              {mineVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {mineVisible ? 'My map is showing' : 'Show my map'}
            </Button>
            {room.isHost && (
              <Button variant="secondary" size="sm" onClick={room.revealAll}>
                Reveal all maps
              </Button>
            )}
          </>
        )}
      </header>

      <div className="min-h-0 flex-1">
        {mode === 'draw' ? (
          <MapEditor key={editorKey} initialDoc={startDoc} onChange={room.saveMyDoc} legend={(api) => <Legend {...api} />} />
        ) : mode === 'shared' && room.shared ? (
          <SharedModels room={room} shared={room.shared} />
        ) : (
          <Stage room={room} />
        )}
      </div>

      {room.canSaveToSpace && (
        <SaveToSpace open={savingToSpace} onClose={() => setSavingToSpace(false)} doc={room.myDoc} defaultName={room.prompt} />
      )}
      <ExamplesModal
        open={examplesOpen}
        onClose={() => setExamplesOpen(false)}
        onUse={(doc) => (room.myDoc.shapes.length > 0 ? setPendingExample(doc) : loadExample(doc))}
        hasMap={room.myDoc.shapes.length > 0}
        onBlank={() => (room.myDoc.shapes.length > 0 ? setPendingExample(EMPTY_MAP) : setExamplesOpen(false))}
      />
      <Modal
        open={pendingExample !== null}
        onClose={() => setPendingExample(null)}
        size="sm"
        title="Replace your map?"
        description={
          pendingExample && pendingExample.shapes.length === 0
            ? 'Starting over clears what you have drawn so far.'
            : 'Starting from an example replaces what you have drawn so far.'
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setPendingExample(null)}>
              Keep my map
            </Button>
            <Button variant="cta" size="sm" onClick={() => pendingExample && loadExample(pendingExample)}>
              Replace it
            </Button>
          </>
        }
      />
    </div>
  );
}
